/**
 * Warm route chunks before the buyer clicks, so navigation feels instant — the
 * chunk is already downloaded (and HTTP-cached) by the time React Router asks
 * for it. This mirrors how Shopee / TikTok Shop prefetch the checkout bundle
 * from the product page.
 *
 * Each loader specifier MUST match the one used in App.tsx's lazyNamed() call so
 * the bundler resolves both to the SAME chunk hash — otherwise we'd download the
 * page twice instead of sharing one cached module.
 */

import { loadChunk } from "@/lib/chunkLoader";
import { isCheckoutPath, isStorefrontRootPath } from "@shared/storefrontRoutes";

type Loader = () => Promise<unknown>;

const warmed = new Set<string>();

function warm(key: string, loader: Loader) {
  if (warmed.has(key) || typeof window === "undefined") return;
  warmed.add(key);
  // Route through loadChunk in prefetch mode: a failed background warm must never
  // reload the page the buyer is reading (that would blow the one-shot reload
  // budget before the real click). It retries quietly and, on final failure,
  // stays silent — the eventual real navigation recovers on its own.
  void loadChunk(loader, { prefetch: true }).catch(() => warmed.delete(key));
}

function onIdle(fn: () => void) {
  const ric = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;
  if (ric) ric(fn, { timeout: 2500 });
  else window.setTimeout(fn, 1200);
}

/**
 * Run once the initial page has finished loading its own subresources.
 *
 * `requestIdleCallback` alone is NOT enough: the browser counts "waiting on the
 * network" as idle, so an idle callback scheduled at mount fires while the first
 * render is still fetching. That is the exact moment a prefetch must stay out of
 * the way, so gate on `load` first and only then ask for idle.
 */
function afterLoad(fn: () => void) {
  if (document.readyState === "complete") {
    onIdle(fn);
    return;
  }
  window.addEventListener("load", () => onIdle(fn), { once: true });
}

/** Storefront landing chunk — fetched in parallel with entry evaluation. */
export function prefetchStoreHome() {
  warm("store-home", () => import("@/pages/public/StoreHome"));
}

/**
 * Warm StoreHome only when it is genuinely the first thing that will render —
 * the storefront root. Sellers point outside sales-page buttons straight at a
 * `…/checkout` URL, and on that landing StoreHome is never rendered; fetching it
 * anyway just took a slot on the connection the checkout's own chunks needed.
 */
export function prefetchStoreHomeIfLanding(pathname = window.location.pathname) {
  if (!isStorefrontRootPath(pathname)) return;
  prefetchStoreHome();
}

/** Page + single-product renderers reached from the bio-web store home. */
export function prefetchStorePages() {
  warm("page-view", () => import("@/pages/public/PublicPageView"));
  warm("store-product", () => import("@/pages/public/StoreProductPage"));
}

/** The checkout surfaces a buyer reaches from a sales / product page. */
export function prefetchCheckout() {
  warm("checkout-view", () => import("@/pages/public/PublicCheckoutView"));
  warm("store-checkout", () => import("@/pages/public/StoreCheckout"));
}

/** The FYP single-product module, now: a product page is what is opening, so
 *  its download runs alongside the product's read instead of after it. */
export function warmFypProduct() {
  warm("fyp-product", () => import("@/components/fyp/FypProductPage"));
}

/** A profile's Shop: its products' module is fetched once the page has loaded,
 *  so the tap finds it cached. */
export function warmFypProductOnIdle() {
  afterLoad(warmFypProduct);
}

/** Where a FYP feed's posts open their products, each in a sheet. */
export type FypSheetPages = { product: boolean; sales: boolean; checkout: boolean };

/**
 * The pages a FYP feed's posts open in a sheet — a frame that boots that page's
 * route anew — fetched once the feed has loaded, so the tap finds them cached.
 * Only the kinds its posts open: the rest of the bandwidth is the videos'.
 */
export function warmFypSheetsOnIdle(pages: FypSheetPages) {
  afterLoad(() => {
    if (pages.product) {
      warm("fyp-profile", () => import("@/pages/PublicFypProfilePage"));
      warmFypProduct();
    }
    if (pages.sales) warm("page-view", () => import("@/pages/public/PublicPageView"));
    if (pages.checkout) warm("checkout-view", () => import("@/pages/public/PublicCheckoutView"));
  });
}

/**
 * Warm the buyer-funnel chunks that are still AHEAD of the buyer, once the page
 * they are on has finished loading.
 *
 * Two things this deliberately does NOT do, both measured on prod (session 71F):
 *
 * 1. It does not run on a checkout path. StoreHome / PublicPageView /
 *    StoreProductPage cannot be the next render there, yet warming them (plus
 *    their own deps — SingleProductView, CodePreviewFrame) opened five more
 *    requests on the same connection while the checkout's critical chunks
 *    (useFetch, usePublicPage, PublicCheckoutView) were still queued behind
 *    them. Nothing on the checkout paints until its page-data fetch returns, and
 *    that fetch cannot even start until those chunks land: it left the browser
 *    at 2.5s on a cold load.
 * 2. It does not fire on mount. See `afterLoad` — an idle callback scheduled at
 *    mount lands in the middle of the initial render, not after it.
 */
export function prefetchBuyerFunnelOnIdle(pathname = window.location.pathname) {
  if (isCheckoutPath(pathname)) return;
  afterLoad(() => {
    prefetchStorePages();
    prefetchCheckout();
  });
}
